import { Component, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { DynamicFilterConfig, createDynamicFilterState } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorState } from 'primeng/paginator';
import { SortEvent } from 'primeng/api';
import _ from 'lodash';
import {
  PettyCashFund,
  PettyCashFundSearchCommand,
  PettyCashFundStatus,
  PettyCashFundSummary,
} from '../../../models/petty-cash-fund/petty-cash-fund';
import { PettyCashRoles } from '../../../models/roles/roles-group';
import { PettyCashFundProxyService } from '../../../proxies/petty-cash-fund.proxy.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { CURRENCY_OPTIONS, Currency, currencySymbol } from '../../../utilities/currency.util';
import { fundStatusSeverity, fundStatusLabel } from '../petty-cash-fund-status.util';

/** The filter panel's controls; select values arrive as strings. */
interface FundFilterFormModel {
  search: string | null;
  status: string | null;
  currency: string | null;
}

type FundFilters = Partial<FundFilterFormModel>;

/**
 * Petty cash funds (#5785) — cash held by a custodian and replenished from the bank as it is spent. The fund's
 * balance only changes through fund movements; this list is master data with its authorized amounts.
 */
@Component({
  selector: 'app-petty-cash-funds',
  templateUrl: './funds.component.html',
})
export class PettyCashFundsComponent {
  private readonly proxy = inject(PettyCashFundProxyService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly userAuth = inject(UserAuthenticationService);

  private currentSearchCriteria: PettyCashFundSearchCommand = {};

  readonly canEdit = this.userAuth.roles.includes(PettyCashRoles.PettyCashFundAdmin);

  readonly isLoading = signal(0);
  readonly showFilters = signal(true);
  readonly filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'petty-cash-fund-filters', config: this.filterConfig });
  readonly items = signal<PettyCashFund[]>([]);
  readonly summary = signal<PettyCashFundSummary | null>(null);
  readonly totalRecords = signal(0);
  readonly currentPage = signal(1);
  readonly pageSize = signal(25);
  readonly sortField = signal<string | null>(null);
  readonly sortOrder = signal(-1);

  /** "L 70,000.00 · $ 1,000.00" — one figure per currency the active funds hold. */
  readonly authorizedLabel = computed(() => {
    const amounts = this.summary()?.authorizedByCurrency ?? [];
    if (amounts.length === 0) return '—';
    return amounts
      .map(
        (a) => `${currencySymbol(a.currency)} ${a.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      )
      .join(' · ');
  });

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters as FundFilters));

    effect(
      () => {
        if (this.translateService.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
    this.loadSummary();
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private t(key: string, fallback: string): string {
    const value = this.translateService.instant(key);
    return value === key ? fallback : value;
  }

  private tryInitializeFilterConfig(): void {
    this.filterConfig.set(this.buildFilterConfig());
  }

  private buildFilterConfig(): DynamicFilterConfig {
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'search',
          label: this.t('PettyCashFund_Filter_Search', 'Number, name or custodian'),
          placeholder: this.t('PettyCashFund_Filter_Search_Placeholder', 'Search by number, name or custodian'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'status',
          label: this.t('Status', 'Status'),
          placeholder: this.t('Select_status', 'Select status'),
          options: [
            PettyCashFundStatus.Draft,
            PettyCashFundStatus.ReadyForFunding,
            PettyCashFundStatus.Active,
            PettyCashFundStatus.Closed,
          ].map((status) => ({
            label: fundStatusLabel(status, (k, f) => this.t(k, f)),
            value: String(status),
          })),
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'currency',
          label: this.t('PettyCashFund_Field_Currency', 'Currency'),
          placeholder: this.t('PettyCashFund_Select_Currency', 'Select currency'),
          options: CURRENCY_OPTIONS.filter((c) => c.value !== Currency.EUR).map((c) => ({ label: c.label, value: String(c.value) })),
          showClear: true,
          defaultVisible: false,
        },
      ],
    };
  }

  private searchWithFilters(filters: FundFilters): void {
    const search = (filters.search ?? '').toString().trim();
    const status = _.isNil(filters.status) || filters.status === '' ? null : (Number(filters.status) as PettyCashFundStatus);
    const currency = _.isNil(filters.currency) || filters.currency === '' ? null : (Number(filters.currency) as Currency);
    this.currentSearchCriteria = { search: search || null, statuses: status === null ? null : [status], currency };
    this.currentPage.set(1);
    this.search(this.currentSearchCriteria);
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.search(this.currentSearchCriteria);
  }

  onSort(event: SortEvent): void {
    const newField = event.field ?? null;
    const newOrder = event.order ?? 1;
    if (newField === this.sortField() && newOrder === this.sortOrder()) return;
    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.search(this.currentSearchCriteria);
  }

  private search(criteria: PettyCashFundSearchCommand): void {
    this.startLoading();
    this.proxy
      .search(criteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        this.items.set(response.data?.items ?? []);
        this.totalRecords.set(response.data?.totalCount ?? 0);
      });
  }

  private loadSummary(): void {
    this.proxy.getSummary().subscribe((response) => this.summary.set(response.data ?? null));
  }

  redirectToDetails(id: string): void {
    this.router.navigate(['/petty-cash-fund-details'], { queryParams: { id } });
  }

  createFund(): void {
    this.redirectToDetails('new');
  }

  symbolOf(currency: Currency): string {
    return currencySymbol(currency);
  }

  statusLabel(status: PettyCashFundStatus): string {
    return fundStatusLabel(status, (k, f) => this.t(k, f));
  }

  statusSeverity(status: PettyCashFundStatus): 'success' | 'secondary' | 'warn' | 'info' {
    return fundStatusSeverity(status);
  }

  policyLabel(row: PettyCashFund): string {
    const name = row.claimPolicyName;
    if (_.isNil(name)) return this.t('PettyCashFund_Policy_Default', 'Default claim policy');
    return this.translateService.currentLang === 'es' ? name.es : name.en;
  }

  getRow(rowData: unknown): PettyCashFund {
    return rowData as PettyCashFund;
  }
}
